import { Controller } from "@hotwired/stimulus"

// Export portfolio configuration modal (Step 20).
// Fetches the bundle inline on open to show category counts, then on
// Download click it re-fetches as an attachment so the server fires
// `Portfolio Config Exported`.
export default class extends Controller {
  static targets = [
    "loading", "preview", "errors", "download",
    "themeCount", "thresholdsCount", "presetCount", "alertCount", "reportCount"
  ]
  static values = { exportUrl: String }

  connect() {
    // The generic modal controller toggles a `hidden` class on this element
    // to open/close; we watch the class attribute and re-fetch the preview
    // whenever it becomes visible.
    this._observer = new MutationObserver(() => {
      if (!this.element.classList.contains("hidden")) this._loadPreview()
    })
    this._observer.observe(this.element, { attributes: true, attributeFilter: ["class"] })
  }

  disconnect() {
    this._observer?.disconnect()
  }

  async _loadPreview() {
    this._showLoading()
    try {
      const res = await fetch(this.exportUrlValue, {
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const bundle = await res.json()
      this._render(bundle)
    } catch (err) {
      console.error("Config export preview failed", err)
      this._renderError("Could not read the current configuration.")
    }
  }

  _showLoading() {
    this.loadingTarget.classList.remove("hidden")
    this.previewTarget.classList.add("hidden")
    this.errorsTarget.classList.add("hidden")
    this.downloadTarget.disabled = true
  }

  _render(bundle) {
    this.loadingTarget.classList.add("hidden")
    this.previewTarget.classList.remove("hidden")
    this.downloadTarget.disabled = false

    const sections = bundle?.sections || {}
    const themeKeys = Object.keys(sections.theme || {}).length
    const thresholdsOverrides = Object.keys((sections.rag_thresholds || {}).overrides || {}).length

    this.themeCountTarget.textContent = themeKeys > 0 ? `${themeKeys} key${themeKeys === 1 ? "" : "s"} customized` : "Defaults"
    const profile = (sections.rag_thresholds || {}).profile || "balanced"
    const profileLabel = profile.charAt(0).toUpperCase() + profile.slice(1)
    this.thresholdsCountTarget.textContent = `${profileLabel}${thresholdsOverrides > 0 ? ` · ${thresholdsOverrides} override${thresholdsOverrides === 1 ? "" : "s"}` : ""}`

    this.presetCountTarget.textContent = this._countLabel(sections.filter_presets, "preset")
    this.alertCountTarget.textContent = this._countLabel(sections.alert_rules, "rule")
    this.reportCountTarget.textContent = this._countLabel(sections.scheduled_reports, "schedule")
  }

  _countLabel(list, singular) {
    const n = Array.isArray(list) ? list.length : 0
    if (n === 0) return "None"
    return `${n} ${singular}${n === 1 ? "" : "s"}`
  }

  _renderError(msg) {
    this.loadingTarget.classList.add("hidden")
    this.errorsTarget.textContent = msg
    this.errorsTarget.classList.remove("hidden")
    this.downloadTarget.disabled = true
  }

  download(event) {
    event?.preventDefault()
    // Hitting the same URL without an `Accept: application/json` header
    // causes the server to send the file as an attachment and emit the
    // Mixpanel event. `window.location` is the simplest way to trigger the
    // browser's native download UI.
    window.location.href = this.exportUrlValue
  }
}
